import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { portalApi, portalDate } from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";

const Bubble = ({ m }) => {
  const mine = m.author === "customer";
  return (
    <div
      className={`flex ${mine ? "justify-end" : "justify-start"}`}
      data-testid={`portal-message-${m.id}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
          mine
            ? "bg-[#c39e6d] text-white"
            : "border border-slate-200 bg-white text-slate-700"
        }`}
      >
        <div className="whitespace-pre-wrap leading-relaxed">{m.body}</div>
        <div
          className={`mt-1 text-[10px] uppercase tracking-widest ${
            mine ? "text-slate-400" : "text-slate-400"
          }`}
        >
          {m.author_name || (mine ? "You" : "Our team")} ·{" "}
          {portalDate(m.created_at)}
        </div>
      </div>
    </div>
  );
};

export const PortalMessages = ({ subjectType, subjectId }) => {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await portalApi.get("/messages", {
      params: { subject_type: subjectType, subject_id: subjectId },
    });
    setItems(data);
  }, [subjectType, subjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await portalApi.post("/messages", {
        subject_type: subjectType,
        subject_id: subjectId,
        body: text.trim(),
      });
      setItems(data.messages);
      setText("");
      toast.success("Note sent — we will get back to you");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send that note",
      );
    }
    setBusy(false);
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="portal-messages"
    >
      <h2 className="text-base font-semibold text-slate-900 md:text-lg">
        Notes with our team
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        Ask a question or leave a note — it lands straight in the shop.
      </p>
      <div className="mt-4 space-y-3">
        {items.length === 0 && (
          <p
            className="text-sm text-slate-400"
            data-testid="portal-messages-empty"
          >
            No notes yet.
          </p>
        )}
        {items.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <textarea
          data-testid="portal-message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Type your note…"
          className="min-h-[52px] flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors duration-150 focus:border-teal-500"
        />
        <button
          type="button"
          data-testid="portal-message-send"
          disabled={busy || !text.trim()}
          onClick={send}
          className="flex h-[52px] items-center justify-center gap-2 rounded-xl bg-[#c39e6d] px-5 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#a9855a] disabled:opacity-40"
        >
          <PaperPlaneTilt size={14} weight="bold" />
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </section>
  );
};
